import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { LogIn, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";

export default function EmployeeAttendance() {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());

  const load = () => {
    api.get("/attendance/history").then((r) => setItems(r.data)).catch(() => {});
    api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const clockIn = async () => {
    setLoading(true);
    try { await api.post("/attendance/clock-in"); toast.success("Clocked in"); load(); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  const clockOut = async () => {
    setLoading(true);
    try { await api.post("/attendance/clock-out"); toast.success("Clocked out"); load(); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB") : "—";
  const loggedIn = today?.login_time && !today?.logout_time;
  const done = today?.login_time && today?.logout_time;

  // summary
  const totalDays = items.length;
  const fullDays = items.filter((r) => (r.working_hours || 0) >= 3).length;
  const totalHours = items.reduce((s, r) => s + (r.working_hours || 0), 0).toFixed(2);

  return (
    <div className="space-y-6" data-testid="employee-attendance-page">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">01 / Log</div>
        <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Attendance.</h1>
      </div>

      {/* Clock In/Out panel */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-zinc-950 text-zinc-50 p-8 flex items-center justify-between">
          <div>
            <div className="eyebrow text-zinc-400 flex items-center gap-2"><Clock size={12} /> Live · {now.toLocaleDateString("en-GB")}</div>
            <div className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100 mt-2 tabular-nums">{now.toLocaleTimeString("en-GB")}</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={clockIn} disabled={loading || !!today?.login_time} className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-12 px-6 disabled:opacity-40" data-testid="attendance-clock-in-btn">
              <LogIn size={14} className="mr-2" /> Check In
            </Button>
            <Button onClick={clockOut} disabled={loading || !loggedIn} className="rounded-xl bg-white text-zinc-950 hover:bg-zinc-100 dark:bg-zinc-800 uppercase tracking-[0.2em] text-xs font-bold h-12 px-6 disabled:opacity-40" data-testid="attendance-clock-out-btn">
              <LogOut size={14} className="mr-2" /> Check Out
            </Button>
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6 space-y-3">
          <div className="eyebrow">Today</div>
          <div className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock-In</span><span className="font-mono">{fmt(today?.login_time)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock-Out</span><span className="font-mono">{fmt(today?.logout_time)}</span></div>
            <div className="flex justify-between pt-2 border-t border-zinc-100 dark:border-zinc-800"><span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Hours</span><span className="font-mono font-bold">{today?.working_hours || 0}</span></div>
          </div>
          {done && <div className="eyebrow text-emerald-600 pt-2 border-t border-zinc-100 dark:border-zinc-800">Attendance marked</div>}
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatBox label="Total Logged Days" value={totalDays} />
        <StatBox label="Full Days (≥3 hrs)" value={fullDays} />
        <StatBox label="Total Hours" value={totalHours} />
      </div>

      {/* History table */}
      <div>
        <div className="eyebrow mb-3">History Report</div>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Date</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock In</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock Out</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Hours</TableHead>
                <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Classification</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">No attendance records yet.</TableCell></TableRow>
              ) : items.map((a) => {
                const h = a.working_hours || 0;
                const cls = h >= 3 ? "FULL DAY" : h > 0 ? "HOURLY" : "—";
                const color = cls === "FULL DAY" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : cls === "HOURLY" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-zinc-100 dark:bg-zinc-800";
                return (
                  <TableRow key={a.date} data-testid={`attendance-row-${a.date}`}>
                    <TableCell className="font-mono">{a.date}</TableCell>
                    <TableCell className="font-mono">{fmt(a.login_time)}</TableCell>
                    <TableCell className="font-mono">{fmt(a.logout_time)}</TableCell>
                    <TableCell className="font-mono font-bold">{h}</TableCell>
                    <TableCell><span className={`eyebrow px-2 py-1 border ${color}`}>{cls}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const StatBox = ({ label, value }) => (
  <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-5">
    <div className="eyebrow">{label}</div>
    <div className="rule-text text-3xl mt-2">{value}</div>
  </div>
);
